import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Video, RefreshCw, Clock, AlertTriangle, Maximize2, Grid2x2, Square,
  Gauge, Wifi, WifiOff, ChevronDown, Search, Check, Play, StopCircle, Camera
} from 'lucide-react';
import api from '../services/api';

// ── go2rtc web component (MSE por WebSocket, H.264 real) ───────────────────────
let _loading = false;
function loadGo2rtc() {
  if (_loading || (window.customElements && customElements.get('video-stream'))) return;
  _loading = true;
  const sc = document.createElement('script');
  sc.type = 'module';
  sc.src = '/go2rtc/video-stream.js';
  sc.onerror = () => { _loading = false; };
  document.head.appendChild(sc);
}

// Tope de la sesión de vivo EN EL PANEL, en minutos.
//
// Por qué existe: el vivo por túnel ISUP hace que el MDVR suba video continuo,
// ~180-270 MB/hora en SD y ~900 MB/h en HD. Si la unidad sale por el hotspot de
// Samsara (500 MB al mes, se reinicia el día 1) una pestaña olvidada se come el
// plan del mes en una noche y el equipo se queda sin plataforma. Antes el vivo
// arrancaba SOLO al abrir la página —se autoselecciona el primer equipo en línea—
// y nada lo paraba nunca.
const TOPE_MIN = 20;

function VideoPlayer({ name, onState, muted = true }) {
  const ref = useRef(null);
  const token = localStorage.getItem('token') || '';
  const src = '/go2rtc/api/ws?src=' + encodeURIComponent(name) + '&token=' + encodeURIComponent(token);
  // Aplica mute/unmute (unmute debe venir de un gesto del usuario: el clic al botón).
  useEffect(() => {
    const v = ref.current && ref.current.video;
    if (v) { v.muted = muted; if (!muted) { const p = v.play && v.play(); if (p && p.catch) p.catch(() => {}); } }
  }, [muted]);
  useEffect(() => {
    let alive = true;
    loadGo2rtc();
    if (!window.customElements) return undefined;
    customElements.whenDefined('video-stream').then(() => {
      if (!alive || !ref.current) return;
      const el = ref.current;
      try { el.mode = 'mse'; el.background = true; el.src = src; } catch (e) { /* noop */ }

      // El navegador NO autoreproduce video con audio: hay que silenciarlo. El
      // <video> interno lo crea el web component de forma asíncrona -> reintentar.
      let tries = 0;
      const arm = () => {
        if (!alive) return;
        const v = el.video;
        if (v) {
          v.muted = true; v.defaultMuted = true;
          v.autoplay = true; v.playsInline = true;
          v.removeAttribute('controls');
          const p = v.play && v.play();
          if (p && p.catch) p.catch(() => {});
          if (onState) {
            // Gracia anti-parpadeo: un micro-corte de MSE NO debe mostrar
            // "Conectando…". Solo si el corte dura >5s se considera pérdida real.
            let waitTimer = null;
            const clearWait = () => { if (waitTimer) { clearTimeout(waitTimer); waitTimer = null; } };
            const playing = () => { clearWait(); onState('playing'); };
            v.onplaying = playing;
            v.onplay = playing;
            v.ontimeupdate = playing;           // el video avanza -> ya reproduce
            v.onwaiting = () => {                // rebuffer: esperar 5s antes de avisar
              clearWait();
              waitTimer = setTimeout(() => onState('loading'), 5000);
            };
            v.onstalled = v.onwaiting;
            v.onerror = () => { clearWait(); onState('error'); };
            v._clearWait = clearWait;
            if (!v.paused && v.readyState >= 2) onState('playing');
          }
        } else if (tries++ < 40) {
          setTimeout(arm, 100);
        }
      };
      arm();
    });
    return () => { alive = false; };
  }, [src]);
  return React.createElement('video-stream', { ref, key: name, class: 'vstream' });
}

// ── Player N9M (MDVR EPCOM/XMR) ──────────────────────────────
// N9M no es RTSP ni pasa por el túnel ISUP: el video sale como MJPEG de la API
// del servidor N9M, que el navegador pinta en un <img> sin librerías.
//
// Y el equipo entrega UN SOLO flujo a la vez (INTEGRACION_N9M §11), así que el
// vivo no puede quedarse puesto como en RTSP/ISUP. Se copia el modelo de la
// página de referencia del propio servidor N9M:
//   reposo -> una imagen cada 60 s (toma un frame y suelta el flujo)
//   vivo   -> MJPEG continuo, explícito, que ocupa el flujo hasta pararlo
function N9MPlayer({ deviceId, channel, live }) {
  const token = localStorage.getItem('token') || '';
  const [tick, setTick] = useState(() => Date.now());
  const [failed, setFailed] = useState(false);

  // Modo imagen: refresca cada 60 s. En vivo no aplica, el MJPEG ya fluye solo.
  useEffect(() => {
    setFailed(false);
    if (live) return undefined;
    setTick(Date.now());
    const id = setInterval(() => setTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, [live, deviceId, channel]);

  if (!deviceId) return <div style={s.placeholder}>Selecciona un dispositivo</div>;

  const q = `dev=${encodeURIComponent(deviceId)}&ch=${channel}`
    + `&token=${encodeURIComponent(token)}&t=${tick}`;
  // Al cambiar de src el navegador aborta la peticion anterior; el proxy corta
  // el upstream y el equipo suelta su unico flujo.
  const src = `/n9m/${live ? 'video.mjpg' : 'snapshot.jpg'}?${q}`;

  return (
    <>
      <img key={src} src={src} alt="" style={s.n9mImg}
           onLoad={() => setFailed(false)} onError={() => setFailed(true)} />
      {failed && (
        <div style={s.overlayErr}>
          <WifiOff size={30} />
          <div>{live ? 'No se pudo abrir el vivo' : 'Sin imagen de esta cámara'}</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            el equipo puede estar ocupado (un solo flujo a la vez)
          </div>
          <button style={s.retry} onClick={() => setTick(Date.now())}>Reintentar</button>
        </div>
      )}
    </>
  );
}

// ── Reposo: lo que se ve cuando el vivo NO está puesto ───────────────────────
// Antes no existía: el reproductor se montaba en cuanto la página elegía un equipo
// —y elige el primero en línea sola—, así que abrir /vivo ya ponía al MDVR a subir
// video sin que nadie lo hubiera pedido.
function Reposo({ porTiempo, onVer, hayEquipo, hayLista }) {
  if (!hayEquipo) {
    return (
      <div style={s.placeholder}>
        {hayLista ? 'Selecciona un dispositivo' : 'Ningún equipo en línea'}
      </div>
    );
  }
  return (
    <div style={s.reposo}>
      <div style={s.reposoTit}>
        {porTiempo ? `Vivo detenido a los ${TOPE_MIN} min` : 'Vivo detenido'}
      </div>
      <div style={s.reposoNota}>
        {porTiempo
          ? 'Se cortó solo para no seguir gastando datos del equipo. Dale otra vez si lo sigues necesitando.'
          : `Ver en vivo hace que el MDVR suba video continuo: ~200 MB por hora en SD, y hay unidades con 500 MB al mes. Se corta solo a los ${TOPE_MIN} min.`}
      </div>
      <button style={s.liveBtn} onClick={onVer}><Play size={15} /> Ver en vivo</button>
    </div>
  );
}

// ── Selector de equipo con buscador ───────────────────────────────────────
// El <select> nativo no permite buscar y con decenas de equipos obliga a
// scrollear. Este combo filtra por nombre o device_id y se maneja con teclado.
function DeviceSelect({ devices, value, onChange }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi]       = useState(0);      // opción resaltada (teclado/hover)
  const boxRef   = useRef(null);
  const inputRef = useRef(null);
  const listRef  = useRef(null);

  const current = devices.find(d => d.device_id === value);
  const label   = current ? (current.name || current.device_id) : 'Sin equipos en línea';
  const onlineCount = devices.filter(d => d.is_online).length;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter(d =>
      (d.name || '').toLowerCase().includes(q) || (d.device_id || '').toLowerCase().includes(q)
    );
  }, [devices, query]);

  const openMenu = () => {
    setQuery('');
    setHi(Math.max(0, devices.findIndex(d => d.device_id === value)));
    setOpen(true);
  };

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  // Cerrar al hacer clic fuera.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // La lista se refresca sola cada 30s: no dejar el resaltado fuera de rango.
  useEffect(() => { setHi(h => Math.min(h, Math.max(0, shown.length - 1))); }, [shown.length]);

  useEffect(() => {
    const el = listRef.current && listRef.current.children[hi];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [hi, open]);

  const pick = (d) => { onChange(d.device_id); setOpen(false); };

  const onKey = (e) => {
    if (e.key === 'ArrowDown')      { e.preventDefault(); setHi(h => Math.min(h + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter')     { e.preventDefault(); if (shown[hi]) pick(shown[hi]); }
    else if (e.key === 'Escape')    { e.preventDefault(); setOpen(false); }
  };

  return (
    <div ref={boxRef} style={s.selectWrap}>
      <button type="button" style={s.selectBtn} title="Elegir equipo en línea"
              onClick={() => (open ? setOpen(false) : openMenu())}>
        <span style={s.selectLabel}>{label}</span>
        <ChevronDown size={15} color="#94a3b8" />
      </button>

      {open && (
        <div style={s.dd}>
          <div style={s.ddSearch}>
            <Search size={14} color="#94a3b8" />
            <input ref={inputRef} value={query} style={s.ddInput}
                   placeholder="Buscar por nombre o ID…"
                   onChange={e => { setQuery(e.target.value); setHi(0); }}
                   onKeyDown={onKey} />
          </div>

          <div ref={listRef} style={s.ddList}>
            {shown.map((d, i) => (
              <button key={d.device_id} type="button"
                      style={i === hi ? s.ddOptA : s.ddOpt}
                      onMouseEnter={() => setHi(i)}
                      onClick={() => pick(d)}>
                <span style={{ ...s.ddDot, background: d.is_online ? '#22c55e' : '#94a3b8' }} />
                <span style={s.ddName}>{d.name || d.device_id}</span>
                <span style={s.ddId}>{d.device_id}</span>
                {d.device_id === value && <Check size={14} color="#3b82f6" />}
              </button>
            ))}
            {!shown.length && <div style={s.ddEmpty}>Sin resultados</div>}
          </div>

          <div style={s.ddFoot}>{onlineCount} en línea</div>
        </div>
      )}
    </div>
  );
}

// ── Módulo ─────────────────────────────────────────────────────────────────────
const Live = () => {
  const [devices, setDevices]   = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [channel, setChannel]   = useState(1);
  const [stream, setStream]     = useState('sub');    // sub por defecto (confiable en MDVR; HD 1080p pesa y puede tardar/fallar)
  const [transport, setTransport] = useState('rtsp'); // rtsp (LAN) | isup (remoto)
  const [view, setView]         = useState('single'); // single | grid
  const [status, setStatus]     = useState('loading');
  const [autoSd, setAutoSd]     = useState(false);   // cayó a SD porque el HD no conectó
  const [clock, setClock]       = useState(null);
  const [clockErr, setClockErr] = useState(null);
  const [rtspAlive, setRtspAlive] = useState(false);   // ¿responde RTSP (554)? -> vivo va aunque ISUP esté offline
  const [fpsBusy, setFpsBusy]   = useState(false);
  const [showFps, setShowFps]   = useState(false);
  const [n9mLive, setN9mLive]   = useState(false);   // N9M: vivo pedido a mano
  const [playing, setPlaying]   = useState(false);   // RTSP/ISUP: tambien a mano
  const [porTiempo, setPorTiempo] = useState(false); // se corto al cumplir el tope
  const [restaMin, setRestaMin] = useState(0);
  const stageRef = useRef(null);

  // El estado en línea cambia solo (4G que entra y sale): refrescar la lista
  // para que el selector no ofrezca equipos que ya se cayeron.
  useEffect(() => {
    let alive = true;
    const load = () => api.get('/api/devices')
      .then(r => { if (alive) setDevices(r.data || []); })
      .catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const device = useMemo(() => devices.find(d => d.device_id === deviceId), [devices, deviceId]);

  // Los MDVR EPCOM (N9M) no hablan RTSP ni pasan por el túnel ISUP: su video va
  // por el servidor N9M, así que casi todo el resto del módulo no les aplica.
  const isN9M = String(device?.protocol_type || '').toUpperCase() === 'N9M';

  // Soltar el flujo del equipo al cambiar de cámara o de equipo: solo entrega
  // uno a la vez, y si no se libera la siguiente cámara no abre.
  useEffect(() => { setN9mLive(false); }, [deviceId, channel]);

  // Al cambiar de equipo se para: ver otra unidad es otra decisión, y abrir su
  // vivo sin que nadie lo pida es gastar datos de otro plan.
  useEffect(() => { setPlaying(false); setPorTiempo(false); }, [deviceId]);

  const enVivo = isN9M ? n9mLive : playing;

  // TOPE DE TIEMPO. No se renueva solo a propósito: lo que esto ataja es la
  // pestaña olvidada, y renovarla sería dejarla correr toda la noche otra vez.
  // Al cumplirse hay que volver a dar clic. El motor tiene su propio tope
  // (ISUP_LIVE_MAX_SEC) como red de abajo, para cuando el navegador ya no está
  // para cortar nada.
  useEffect(() => {
    if (!enVivo) { setRestaMin(0); return undefined; }
    setRestaMin(TOPE_MIN);
    const corte = setTimeout(() => {
      setPlaying(false); setN9mLive(false); setPorTiempo(true);
    }, TOPE_MIN * 60000);
    const tic = setInterval(() => setRestaMin(m => Math.max(0, m - 1)), 60000);
    return () => { clearTimeout(corte); clearInterval(tic); };
  }, [enVivo]);

  const verEnVivo = useCallback(() => {
    setPorTiempo(false);
    setStatus('loading');
    if (isN9M) setN9mLive(true); else setPlaying(true);
  }, [isN9M]);

  // Solo equipos en línea, de menor a mayor por nombre (numérico: Unidad 2 < Unidad 10).
  const onlineDevices = useMemo(
    () => devices
      .filter(d => d.is_online)
      .sort((a, b) => String(a.name || a.device_id)
        .localeCompare(String(b.name || b.device_id), 'es', { numeric: true })),
    [devices]
  );

  // Si el equipo elegido se cae, se queda en la lista para no dejar el
  // selector en blanco con el video todavía puesto.
  const selectable = useMemo(
    () => (!device || device.is_online) ? onlineDevices : [device, ...onlineDevices],
    [onlineDevices, device]
  );

  // Selección inicial: el primero en línea.
  useEffect(() => {
    if (deviceId || !onlineDevices.length) return;
    setDeviceId(onlineDevices[0].device_id);
  }, [onlineDevices, deviceId]);

  const channels = useMemo(() => {
    if (!device) return [1];
    if (device.active_channels) {
      const p = device.active_channels.split(',').map(s => parseInt(s.trim(), 10)).filter(Boolean);
      if (p.length) return p;
    }
    return Array.from({ length: device.channels || 1 }, (_, i) => i + 1);
  }, [device]);

  useEffect(() => { if (device && !channels.includes(channel)) setChannel(channels[0]); }, [device, channels, channel]);

  useEffect(() => {
    if (!deviceId || isN9M) return;   // /time va por ISAPI: un N9M no lo tiene
    let alive = true;
    const f = () => api.get(`/api/devices/${deviceId}/time`)
      .then(r => { if (alive) { setClock(r.data); setClockErr(null); } })
      .catch(e => { if (alive) { setClock(null); setClockErr(e.response?.data?.detail || 'sin hora'); } });
    f();
    const id = setInterval(f, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [deviceId, isN9M]);

  // ¿El equipo responde RTSP (554)? Entonces el vivo RTSP funciona AUNQUE su
  // ISUP esté offline (caso dashcam). Refresca cada 20s.
  useEffect(() => {
    if (!deviceId || isN9M) { setRtspAlive(false); return; }
    let alive = true;
    let first = true;
    const f = () => api.get(`/api/devices/${deviceId}/playback-mode`)
      .then(r => {
        if (!alive) return;
        const reach = !!r.data?.reachable;
        setRtspAlive(reach);
        // Al cambiar de equipo, elige el transporte solo: RTSP si alcanza la LAN
        // (limpio), ISUP si está remoto (4G). Después el usuario puede cambiarlo.
        if (first) { first = false; setTransport(reach ? 'rtsp' : 'isup'); }
      })
      .catch(() => { if (alive) setRtspAlive(false); });
    f();
    const id = setInterval(f, 20000);
    return () => { alive = false; clearInterval(id); };
  }, [deviceId, isN9M]);

  const nameFor = useCallback(
    (ch) => {
      if (!deviceId) return null;
      if (transport === 'isup') return `isup_${deviceId}_${ch}`;   // vivo por túnel (remoto)
      return `${deviceId}_${ch}${stream === 'sub' ? '_sub' : ''}`;  // RTSP directo (LAN)
    },
    [deviceId, stream, transport]
  );
  const streamName = nameFor(channel);
  useEffect(() => { setStatus('loading'); }, [streamName, view]);

  // Auto-fallback a SD: si el HD (main) no conecta en 8s (equipo con encoder
  // pesado / cargado), cambiar solo a SD para que SIEMPRE se vea algo.
  useEffect(() => {
    if (isN9M || stream !== 'main' || status === 'playing') return undefined;
    const t = setTimeout(() => {
      if (stream === 'main') { setAutoSd(true); setStream('sub'); }
    }, 8000);
    return () => clearTimeout(t);
  }, [isN9M, stream, status, streamName]);

  const toggleFull = () => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  const setFps = (fps) => {
    setShowFps(false);
    if (!deviceId) return;
    if (!window.confirm(
      `Ajustar TODAS las cámaras a ${fps} fps.\nMás fps = más fluido, pero la SD se llena más rápido.\n¿Continuar?`
    )) return;
    setFpsBusy(true);
    api.post(`/api/devices/${deviceId}/set-fps?fps=${fps}`)
      .then(r => alert(`Listo: ${r.data.channels_ok}/${r.data.channels_total} cámaras a ${r.data.fps} fps.`))
      .catch(e => alert('No se pudo cambiar fps: ' + (e.response?.data?.detail || 'error')))
      .finally(() => setFpsBusy(false));
  };

  const driftLabel = (sec) => {
    if (sec == null) return null;
    const a = Math.abs(sec);
    if (a < 60) return `${sec}s`;
    if (a < 3600) return `${Math.round(sec / 60)} min`;
    if (a < 86400) return `${Math.round(sec / 3600)} h`;
    return `${Math.round(sec / 86400)} d`;
  };
  const driftBad = clock && clock.drift_seconds != null && Math.abs(clock.drift_seconds) > 120;
  // "En línea" si el equipo está online por ISUP O si responde RTSP (vivo LAN).
  const online = device?.is_online || rtspAlive;
  // La rejilla pide los 4 canales a la vez: imposible en N9M (§11).
  const canGrid = !isN9M;
  const rtspOnly = !device?.is_online && rtspAlive;   // vivo RTSP pero ISUP offline

  return (
    <div style={s.page}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={s.header}>
        <div style={s.brand}>
          <div style={s.brandIcon}><Video size={20} /></div>
          <div>
            <div style={s.brandTitle}>Video en vivo</div>
            <div style={s.brandSub}>
              {isN9M
                ? 'MJPEG · vía servidor N9M (MDVR EPCOM)'
                : (transport === 'isup'
                    ? 'H.264 · vía túnel ISUP (funciona remoto)'
                    : 'H.264 · RTSP directo (LAN)')}
            </div>
          </div>
        </div>

        <div style={s.headerRight}>
          <DeviceSelect devices={selectable} value={deviceId} onChange={setDeviceId} />
          <span style={{ ...s.pill, ...(online ? s.pillOn : s.pillOff) }}>
            {online ? <Wifi size={13} /> : <WifiOff size={13} />}
            {online ? (rtspOnly ? 'En línea (RTSP)' : 'En línea') : 'Offline'}
          </span>
        </div>
      </div>

      {/* Stage */}
      <div ref={stageRef} style={s.stage}>
        {view === 'single' ? (
          <div style={s.single}>
            {isN9M
              ? <N9MPlayer deviceId={deviceId} channel={channel} live={n9mLive} />
              : (streamName && playing
                  ? <VideoPlayer name={streamName} onState={setStatus} />
                  : <Reposo porTiempo={porTiempo} onVer={verEnVivo}
                           hayEquipo={!!streamName} hayLista={!!selectable.length} />)}
            {enVivo && (
              <div style={s.liveTag}>
                <span style={s.liveDot} /> EN VIVO
                {restaMin > 0 && <span style={s.liveResta}>· corta en {restaMin} min</span>}
              </div>
            )}
            {isN9M && !n9mLive && (
              <div style={s.imgTag}><Camera size={12} /> IMAGEN · cada 60 s</div>
            )}
            <div style={s.camTag}>Cam {channel}
              {!isN9M && <> · {stream === 'main' ? 'HD' : 'SD'}</>}
              {!isN9M && autoSd && stream === 'sub' && <span style={{ color: '#fbbf24', marginLeft: 6 }}>· HD no disponible</span>}
            </div>
            {!isN9M && playing && status === 'loading' && streamName && (
              <div style={s.overlay}><RefreshCw size={30} className="spin" /><div>Conectando…</div>
                {stream === 'main' && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>si el HD tarda, cambio a SD solo…</div>}
              </div>
            )}
            {!isN9M && status === 'error' && (
              <div style={s.overlayErr}>
                <WifiOff size={30} />
                <div>Sin señal en esta cámara</div>
                <button style={s.retry} onClick={() => setStatus('loading')}>Reintentar</button>
              </div>
            )}
          </div>
        ) : (
          <div style={s.grid}>
            {channels.map(ch => (
              <div key={ch} style={s.gridCell} onClick={() => { setChannel(ch); setView('single'); }}>
                {/* La rejilla son TANTAS sesiones como cámaras: tampoco arranca sola. */}
                {playing
                  ? <VideoPlayer name={nameFor(ch)} />
                  : <div style={s.gridVacia}>sin vivo</div>}
                <div style={s.gridCellTag}><span style={s.liveDotSm} /> Cam {ch}</div>
              </div>
            ))}
          </div>
        )}

        {/* Controles flotantes sobre el stage */}
        <div style={s.floatBar}>
          <div style={s.segment}>
            <button onClick={() => setView('single')} style={view === 'single' ? s.segBtnA : s.segBtn} title="Una cámara">
              <Square size={15} />
            </button>
            {canGrid && (
              <button onClick={() => setView('grid')} style={view === 'grid' ? s.segBtnA : s.segBtn} title="Rejilla">
                <Grid2x2 size={15} />
              </button>
            )}
          </div>
          <button style={s.iconBtn} onClick={toggleFull} title="Pantalla completa"><Maximize2 size={16} /></button>
        </div>
      </div>

      {/* Barra de cámaras + controles */}
      <div style={s.dock}>
        <div style={s.camStrip}>
          {channels.map(ch => (
            <button key={ch}
              onClick={() => { setChannel(ch); setView('single'); }}
              style={(view === 'single' && ch === channel) ? s.camChipA : s.camChip}>
              Cam {ch}
            </button>
          ))}
        </div>

        <div style={s.dockRight}>
          {isN9M ? (
            <>
              <span style={s.n9mHint}>un solo flujo a la vez</span>
              <button
                style={n9mLive ? s.liveBtnOn : s.liveBtn}
                onClick={() => (n9mLive ? setN9mLive(false) : verEnVivo())}
                title={n9mLive
                  ? 'Detener el vivo y liberar el flujo del equipo'
                  : `Abrir el vivo (ocupa el único flujo del equipo; se corta solo a los ${TOPE_MIN} min)`}>
                {n9mLive ? <><StopCircle size={15} /> Detener</> : <><Play size={15} /> Ver en vivo</>}
              </button>
            </>
          ) : (
          <>
          <button
            style={playing ? s.liveBtnOn : s.liveBtn}
            disabled={!streamName}
            onClick={() => (playing ? setPlaying(false) : verEnVivo())}
            title={playing
              ? 'Detener el vivo y dejar de gastar datos del equipo'
              : `Abrir el vivo (se corta solo a los ${TOPE_MIN} min)`}>
            {playing ? <><StopCircle size={15} /> Detener</> : <><Play size={15} /> Ver en vivo</>}
          </button>

          <div style={s.segment} title="RTSP: directo por LAN (limpio) · ISUP: por el túnel (funciona remoto/4G)">
            <button onClick={() => setTransport('rtsp')} style={transport === 'rtsp' ? s.segBtnA : s.segBtn}>RTSP</button>
            <button onClick={() => setTransport('isup')} style={transport === 'isup' ? s.segBtnA : s.segBtn}>ISUP</button>
          </div>

          {transport === 'rtsp' && (
            <div style={s.segment}>
              <button onClick={() => { setAutoSd(false); setStream('main'); }} style={stream === 'main' ? s.segBtnA : s.segBtn}>HD</button>
              <button onClick={() => { setAutoSd(false); setStream('sub'); }}  style={stream === 'sub'  ? s.segBtnA : s.segBtn}>SD</button>
            </div>
          )}

          <div style={{ position: 'relative' }}>
            <button style={s.ctlBtn} disabled={fpsBusy} onClick={() => setShowFps(v => !v)} title="Fluidez (fps)">
              <Gauge size={15} /> Fluidez <ChevronDown size={13} />
            </button>
            {showFps && (
              <div style={s.fpsMenu}>
                <div style={s.fpsHint}>Aplica a todas las cámaras</div>
                {[6, 12, 15, 22].map(f => (
                  <button key={f} style={s.fpsItem} onClick={() => setFps(f)}>{f} fps</button>
                ))}
              </div>
            )}
          </div>
          </>
          )}
        </div>
      </div>

      {/* Pie: hora del equipo */}
      <div style={s.foot}>
        <div style={s.footItem}>
          {isN9M ? (
            <span style={{ color: '#64748b' }}>
              MDVR EPCOM · protocolo N9M · {device?.channels || 4} cámaras
            </span>
          ) : (
          <>
          <Clock size={14} color={driftBad ? '#f59e0b' : '#60a5fa'} />
          {clock ? (
            <>
              <span>Hora equipo <strong style={{ color: '#e5e7eb' }}>{clock.local_time || '—'}</strong></span>
              {clock.drift_seconds != null && (
                <span style={driftBad ? s.driftBad : s.driftOk}>
                  {driftBad && <AlertTriangle size={12} style={{ verticalAlign: -2, marginRight: 3 }} />}
                  desfase {driftLabel(clock.drift_seconds)}
                </span>
              )}
            </>
          ) : <span style={{ color: '#64748b' }}>{clockErr ? `Hora no disponible` : 'Consultando hora…'}</span>}
          </>
          )}
        </div>
        {device && <div style={s.footId}>{device.device_id} · {device.ip_address || 'sin IP'}</div>}
      </div>
    </div>
  );
};

const CSS = `
  .vstream, video-stream { display:block; width:100%; height:100%; background:#000; }
  .vstream video, video-stream video { width:100%; height:100%; object-fit:contain; background:#000; display:block; }
  .vstream video::-webkit-media-controls, video-stream video::-webkit-media-controls { display:none !important; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes livePulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.35; transform:scale(.8); } }
  .spin { animation: spin 1s linear infinite; }
`;

const RING = '1px solid rgba(255,255,255,0.09)';
const s = {
  page: { padding: 20, maxWidth: 1280, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 12 },
  brand: { display: 'flex', alignItems: 'center', gap: 12 },
  brandIcon: { width: 40, height: 40, borderRadius: 11, background: 'linear-gradient(135deg,#3b82f6,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 6px 16px rgba(59,130,246,.35)' },
  brandTitle: { fontSize: 19, fontWeight: 700, color: '#0f172a', lineHeight: 1.1 },
  brandSub: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 10 },
  selectWrap: { position: 'relative', display: 'inline-flex', alignItems: 'center' },
  selectBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 12px 9px 14px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, fontWeight: 600, color: '#0f172a', cursor: 'pointer', minWidth: 220 },
  selectLabel: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  dd: { position: 'absolute', top: 'calc(100% + 6px)', left: 0, minWidth: 280, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 12px 30px rgba(2,6,23,.18)', zIndex: 30, overflow: 'hidden' },
  ddSearch: { display: 'flex', alignItems: 'center', gap: 7, padding: '9px 12px', borderBottom: '1px solid #f1f5f9' },
  ddInput: { flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#0f172a', background: 'transparent' },
  ddList: { maxHeight: 260, overflowY: 'auto', padding: 4 },
  ddOpt: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  ddOptA: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', border: 'none', background: '#eff6ff', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  ddDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  ddName: { flex: 1, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  ddId: { fontFamily: 'monospace', fontSize: 11, color: '#94a3b8' },
  ddEmpty: { padding: '14px 10px', textAlign: 'center', fontSize: 13, color: '#94a3b8' },
  ddFoot: { padding: '7px 12px', borderTop: '1px solid #f1f5f9', fontSize: 11, color: '#94a3b8' },
  pill: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  pillOn: { background: '#dcfce7', color: '#15803d' },
  pillOff: { background: '#fee2e2', color: '#b91c1c' },

  stage: { position: 'relative', background: 'radial-gradient(120% 120% at 50% 0%, #0f172a 0%, #05070d 100%)', borderRadius: 18, overflow: 'hidden', border: RING, boxShadow: '0 20px 50px rgba(2,6,23,.35)' },
  single: { position: 'relative', aspectRatio: '16 / 9', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  placeholder: { color: '#475569', fontSize: 15 },
  reposo: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center', padding: 24 },
  reposoTit: { color: '#cbd5e1', fontSize: 15, fontWeight: 600 },
  reposoNota: { color: '#64748b', fontSize: 12, maxWidth: 380, lineHeight: 1.5 },
  gridVacia: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: 12 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, aspectRatio: '16 / 9', background: '#000' },
  gridCell: { position: 'relative', background: '#000', overflow: 'hidden', cursor: 'pointer' },
  gridCellTag: { position: 'absolute', left: 8, top: 8, display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(2,6,23,.6)', color: '#e5e7eb', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, backdropFilter: 'blur(4px)' },

  liveResta: { fontWeight: 600, letterSpacing: 0, color: '#cbd5e1', marginLeft: 2 },
  liveTag: { position: 'absolute', left: 12, top: 12, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(2,6,23,.55)', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: .6, padding: '5px 10px', borderRadius: 7, backdropFilter: 'blur(6px)', border: RING },
  liveDot: { width: 8, height: 8, borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 8px #ef4444', animation: 'livePulse 1.4s infinite' },
  liveDotSm: { width: 6, height: 6, borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 6px #ef4444', animation: 'livePulse 1.4s infinite' },
  // N9M: el MJPEG lo pinta un <img>, no el web component de go2rtc.
  n9mImg: { width: '100%', height: '100%', objectFit: 'contain', background: '#000', display: 'block' },
  imgTag: { position: 'absolute', left: 12, top: 12, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(2,6,23,.55)', color: '#cbd5e1', fontSize: 11, fontWeight: 700, letterSpacing: .6, padding: '5px 10px', borderRadius: 7, backdropFilter: 'blur(6px)', border: RING },
  n9mHint: { fontSize: 11, color: '#94a3b8', marginRight: 2 },
  liveBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 11, border: '1px solid transparent', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 6px 16px rgba(59,130,246,.32)' },
  liveBtnOn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 11, border: '1px solid transparent', background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 6px 16px rgba(239,68,68,.32)' },

  camTag: { position: 'absolute', right: 12, top: 12, background: 'rgba(2,6,23,.55)', color: '#cbd5e1', fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 7, backdropFilter: 'blur(6px)', border: RING },

  overlay: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', background: 'rgba(2,6,23,.45)', pointerEvents: 'none', fontSize: 14 },
  overlayErr: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', justifyContent: 'center', color: '#fca5a5', background: 'rgba(2,6,23,.8)', fontSize: 14 },
  retry: { marginTop: 4, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 18px', cursor: 'pointer', fontWeight: 600 },

  floatBar: { position: 'absolute', right: 12, bottom: 12, display: 'flex', alignItems: 'center', gap: 8 },
  iconBtn: { width: 36, height: 36, borderRadius: 10, border: RING, background: 'rgba(2,6,23,.55)', color: '#e5e7eb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' },

  dock: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' },
  camStrip: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  camChip: { padding: '9px 16px', borderRadius: 11, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all .12s' },
  camChipA: { padding: '9px 16px', borderRadius: 11, border: '1px solid transparent', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 6px 16px rgba(59,130,246,.32)' },
  dockRight: { display: 'flex', alignItems: 'center', gap: 8 },
  segment: { display: 'inline-flex', gap: 3, background: 'rgba(2,6,23,.5)', border: RING, padding: 3, borderRadius: 11 },
  segBtn: { border: 'none', background: 'transparent', color: '#cbd5e1', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5 },
  segBtnA: { border: 'none', background: '#3b82f6', color: '#fff', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5 },
  ctlBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 11, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  fpsMenu: { position: 'absolute', right: 0, bottom: 'calc(100% + 6px)', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 12px 30px rgba(2,6,23,.18)', padding: 6, minWidth: 150, zIndex: 20 },
  fpsHint: { fontSize: 11, color: '#94a3b8', padding: '4px 8px 6px' },
  fpsItem: { display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#334155', fontWeight: 600 },

  foot: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 14, padding: '10px 14px', borderRadius: 12, background: '#0f172a', color: '#94a3b8', fontSize: 13, flexWrap: 'wrap' },
  footItem: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  footId: { fontFamily: 'monospace', fontSize: 12, color: '#64748b' },
  driftOk: { color: '#4ade80', fontWeight: 600 },
  driftBad: { color: '#fbbf24', fontWeight: 700, background: 'rgba(251,191,36,.12)', padding: '2px 8px', borderRadius: 6 },
};

export default Live;
